import { supabase } from '../config/supabase';
import {
  scheduleNext,
  parseFlashcardConfig,
  STATUS_PRIORITY,
  type Rating,
  type CardStatus,
  type Sm2Config,
  DEFAULT_SM2_CONFIG,
} from '../lib/spacedRepetition/sm2';
import { formatFlashcardLastSeen } from '../utils/flashcardInsights';

export type { Rating, CardStatus };

export interface FlashcardDeck {
  id: string;
  tuteeId: string;
  name: string;
  description?: string;
  createdBy: string;
  isShared: boolean;
  displayOrder: number;
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
}

export interface Flashcard {
  id: string;
  tuteeId: string;
  deckId?: string;
  studentName?: string;
  front: string;
  back: string;
  tags: string[];
  displayOrder: number;
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
}

export interface FlashcardProgress {
  id: string;
  tuteeId: string;
  studentName: string;
  cardId: string;
  status: CardStatus;
  easeFactor: number | null;
  intervalDays: number;
  dueAt: string;
  learningStep: number;
  lapses: number;
  reps: number;
  lastRating?: Rating;
  lastReviewedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FlashcardQueueItem {
  card: Flashcard;
  progress: FlashcardProgress | null;
  effectiveStatus: CardStatus;
}

export type FlashcardRatingPeriod = 'today' | 'week' | 'month';

export type FlashcardStudyScope = 'all' | 'unassigned' | (string & {});

export function cardMatchesStudyScope(card: Flashcard, scope: FlashcardStudyScope): boolean {
  if (scope === 'all') return true;
  if (scope === 'unassigned') return !card.deckId;
  return card.deckId === scope;
}

export function filterCardsByStudyScope(
  cards: Flashcard[],
  scope: FlashcardStudyScope = 'all'
): Flashcard[] {
  if (scope === 'all') return cards;
  return cards.filter((c) => cardMatchesStudyScope(c, scope));
}

export interface FlashcardStats {
  dueNow: number;
  /** Progress rows created today — cards this student studied for the first time today. */
  newToday: number;
  /** Cards in review with interval ≥ 21 days (graduated from short learning steps). */
  matureCards: number;
  totalActive: number;
  reviewsToday: number;
  streak: number;
  averageEase: number;
  /** @deprecated Use ratingCountsByPeriod.week */
  ratingCounts: Record<Rating, number>;
  ratingCountsByPeriod: Record<FlashcardRatingPeriod, Record<Rating, number>>;
  forecast: { date: string; count: number }[];
  heatmap: { date: string; count: number }[];
}

function emptyRatingCounts(): Record<Rating, number> {
  return { again: 0, hard: 0, good: 0, easy: 0 };
}

function aggregateRatingCounts(
  logRows: { rating: string; reviewed_at: string }[],
  since: Date
): Record<Rating, number> {
  const counts = emptyRatingCounts();
  for (const l of logRows) {
    const t = new Date(l.reviewed_at);
    if (t >= since && l.rating && l.rating in counts) {
      counts[l.rating as Rating] += 1;
    }
  }
  return counts;
}

export type FlashcardModuleConfig = ReturnType<typeof parseFlashcardConfig>;

function mapDeck(row: Record<string, unknown>): FlashcardDeck {
  return {
    id: row.id as string,
    tuteeId: row.tutee_id as string,
    name: row.name as string,
    description: (row.description as string) || undefined,
    createdBy: (row.created_by as string) || 'tutor',
    isShared: row.is_shared !== false,
    displayOrder: (row.display_order as number) ?? 0,
    status: (row.status as 'active' | 'archived') || 'active',
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function mapCard(row: Record<string, unknown>): Flashcard {
  const tags = row.tags;
  return {
    id: row.id as string,
    tuteeId: row.tutee_id as string,
    deckId: (row.deck_id as string) || undefined,
    studentName: (row.student_name as string) || undefined,
    front: row.front as string,
    back: row.back as string,
    tags: Array.isArray(tags) ? (tags as string[]) : [],
    displayOrder: (row.display_order as number) ?? 0,
    status: (row.status as 'active' | 'archived') || 'active',
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function mapProgress(row: Record<string, unknown>): FlashcardProgress {
  return {
    id: row.id as string,
    tuteeId: row.tutee_id as string,
    studentName: row.student_name as string,
    cardId: row.card_id as string,
    status: row.status as CardStatus,
    easeFactor: row.ease_factor != null ? Number(row.ease_factor) : null,
    intervalDays: Number(row.interval_days) || 0,
    dueAt: row.due_at as string,
    learningStep: (row.learning_step as number) ?? 0,
    lapses: (row.lapses as number) ?? 0,
    reps: (row.reps as number) ?? 0,
    lastRating: row.last_rating as Rating | undefined,
    lastReviewedAt: (row.last_reviewed_at as string) || undefined,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

export function resolveStudentSlug(
  tuteeId: string,
  students: { studentName: string }[],
  selected?: string
): string {
  if (selected) return selected.toLowerCase();
  if (students.length === 1) return students[0].studentName.toLowerCase();
  return tuteeId;
}

export async function fetchFlashcardDecks(tuteeId: string): Promise<FlashcardDeck[]> {
  const { data, error } = await supabase
    .from('flashcard_decks')
    .select('*')
    .eq('tutee_id', tuteeId)
    .eq('status', 'active')
    .order('display_order', { ascending: true });
  if (error) throw error;
  return (data || []).map(mapDeck);
}

export async function createFlashcardDeck(input: {
  tuteeId: string;
  name: string;
  description?: string;
  createdBy?: string;
  isShared?: boolean;
}): Promise<FlashcardDeck> {
  const { data, error } = await supabase
    .from('flashcard_decks')
    .insert({
      tutee_id: input.tuteeId,
      name: input.name,
      description: input.description || null,
      created_by: input.createdBy || 'tutor',
      is_shared: input.isShared !== false,
      updated_at: new Date().toISOString(),
    })
    .select()
    .single();
  if (error) throw error;
  return mapDeck(data);
}

export async function updateFlashcardDeck(
  id: string,
  updates: Partial<{ name: string; description: string; status: 'active' | 'archived'; displayOrder: number }>
): Promise<void> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (updates.name != null) payload.name = updates.name;
  if (updates.description != null) payload.description = updates.description;
  if (updates.status != null) payload.status = updates.status;
  if (updates.displayOrder != null) payload.display_order = updates.displayOrder;
  const { error } = await supabase.from('flashcard_decks').update(payload).eq('id', id);
  if (error) throw error;
}

export async function deleteFlashcardDeck(id: string): Promise<void> {
  const { error } = await supabase.from('flashcard_decks').delete().eq('id', id);
  if (error) throw error;
}

/** Find active deck by name or create it (for bulk import). */
export async function findOrCreateFlashcardDeck(
  tuteeId: string,
  name: string
): Promise<FlashcardDeck> {
  const existing = await fetchFlashcardDecks(tuteeId);
  const match = existing.find((d) => d.name.trim().toLowerCase() === name.trim().toLowerCase());
  if (match) return match;
  return createFlashcardDeck({ tuteeId, name: name.trim(), createdBy: 'tutor' });
}

const BULK_INSERT_BATCH = 100;

export async function bulkCreateFlashcards(
  items: Array<{
    tuteeId: string;
    deckId?: string | null;
    studentName?: string;
    front: string;
    back: string;
    displayOrder?: number;
  }>
): Promise<{ created: number }> {
  const now = new Date().toISOString();
  let created = 0;

  for (let i = 0; i < items.length; i += BULK_INSERT_BATCH) {
    const slice = items.slice(i, i + BULK_INSERT_BATCH);
    const payload = slice.map((item, j) => ({
      tutee_id: item.tuteeId,
      deck_id: item.deckId ?? null,
      student_name: item.studentName?.toLowerCase() || null,
      front: item.front.trim(),
      back: item.back.trim(),
      display_order: item.displayOrder ?? i + j,
      status: 'active',
      tags: [],
      updated_at: now,
    }));

    const { error } = await supabase.from('flashcards').insert(payload);
    if (error) throw error;
    created += slice.length;
  }

  return { created };
}

export async function fetchFlashcards(
  tuteeId: string,
  studentSlug: string,
  includeArchived = false
): Promise<Flashcard[]> {
  let query = supabase
    .from('flashcards')
    .select('*')
    .eq('tutee_id', tuteeId)
    .order('display_order', { ascending: true });
  if (!includeArchived) query = query.eq('status', 'active');
  const { data, error } = await query;
  if (error) throw error;
  return (data || [])
    .map(mapCard)
    .filter(
      (c) =>
        !c.studentName ||
        c.studentName.toLowerCase() === studentSlug.toLowerCase()
    );
}

/** All cards for a tuition group (tutor admin — includes student-created cards). */
export async function fetchAllFlashcardsForTutee(
  tuteeId: string,
  includeArchived = false
): Promise<Flashcard[]> {
  let query = supabase
    .from('flashcards')
    .select('*')
    .eq('tutee_id', tuteeId)
    .order('display_order', { ascending: true });
  if (!includeArchived) query = query.eq('status', 'active');
  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map(mapCard);
}

export async function createFlashcard(input: {
  tuteeId: string;
  deckId?: string;
  studentName?: string;
  front: string;
  back: string;
  tags?: string[];
}): Promise<Flashcard> {
  const { data, error } = await supabase
    .from('flashcards')
    .insert({
      tutee_id: input.tuteeId,
      deck_id: input.deckId || null,
      student_name: input.studentName?.toLowerCase() || null,
      front: input.front.trim(),
      back: input.back.trim(),
      tags: input.tags || [],
      updated_at: new Date().toISOString(),
    })
    .select()
    .single();
  if (error) throw error;
  return mapCard(data);
}

export async function updateFlashcard(
  id: string,
  updates: Partial<{ front: string; back: string; deckId: string | null; status: 'active' | 'archived'; displayOrder: number }>
): Promise<void> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (updates.front != null) payload.front = updates.front;
  if (updates.back != null) payload.back = updates.back;
  if (updates.deckId !== undefined) payload.deck_id = updates.deckId;
  if (updates.status != null) payload.status = updates.status;
  if (updates.displayOrder != null) payload.display_order = updates.displayOrder;
  const { error } = await supabase.from('flashcards').update(payload).eq('id', id);
  if (error) throw error;
}

export async function deleteFlashcard(id: string): Promise<void> {
  const { error } = await supabase.from('flashcards').delete().eq('id', id);
  if (error) throw error;
}

export async function fetchProgressForStudent(
  tuteeId: string,
  studentSlug: string
): Promise<FlashcardProgress[]> {
  const { data, error } = await supabase
    .from('flashcard_progress')
    .select('*')
    .eq('tutee_id', tuteeId)
    .eq('student_name', studentSlug);
  if (error) throw error;
  return (data || []).map(mapProgress);
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return startOfDay(a).getTime() === startOfDay(b).getTime();
}

/** First-time study today: progress is created on the student's initial review of a card. */
function countIntroducedToday(progressList: FlashcardProgress[], now: Date): number {
  return progressList.filter((p) => isSameCalendarDay(new Date(p.createdAt), now)).length;
}

export async function buildDueQueue(
  tuteeId: string,
  studentSlug: string,
  moduleConfig: FlashcardModuleConfig,
  studyScope: FlashcardStudyScope = 'all'
): Promise<FlashcardQueueItem[]> {
  const now = new Date();
  const [allCards, progressList] = await Promise.all([
    fetchFlashcards(tuteeId, studentSlug),
    fetchProgressForStudent(tuteeId, studentSlug),
  ]);
  const cards = filterCardsByStudyScope(allCards, studyScope);

  const progressByCard = new Map(progressList.map((p) => [p.cardId, p]));
  const cardIdsInScope = new Set(cards.map((c) => c.id));

  const newIntroducedToday = progressList.filter((p) => {
    if (!cardIdsInScope.has(p.cardId)) return false;
    const created = new Date(p.createdAt);
    return isSameCalendarDay(created, now) && p.reps === 0 && p.status === 'learning';
  }).length;

  const items: FlashcardQueueItem[] = [];

  for (const card of cards) {
    const progress = progressByCard.get(card.id) ?? null;
    const effectiveStatus: CardStatus = progress?.status ?? 'new';

    if (!progress) {
      if (newIntroducedToday < moduleConfig.newCardsPerDay) {
        items.push({ card, progress: null, effectiveStatus: 'new' });
      }
      continue;
    }

    const due = new Date(progress.dueAt);
    if (due <= now) {
      items.push({ card, progress, effectiveStatus });
    }
  }

  items.sort((a, b) => {
    const pa = STATUS_PRIORITY[a.effectiveStatus];
    const pb = STATUS_PRIORITY[b.effectiveStatus];
    if (pa !== pb) return pa - pb;
    const da = a.progress ? new Date(a.progress.dueAt).getTime() : 0;
    const db = b.progress ? new Date(b.progress.dueAt).getTime() : 0;
    return da - db;
  });

  return items.slice(0, moduleConfig.maxReviewsPerDay);
}

export async function rateCard(params: {
  tuteeId: string;
  studentSlug: string;
  card: Flashcard;
  progress: FlashcardProgress | null;
  rating: Rating;
  config: Sm2Config;
  durationMs?: number;
}): Promise<FlashcardProgress> {
  const now = new Date();
  const status: CardStatus = params.progress?.status ?? 'new';
  const input = {
    status,
    easeFactor: params.progress?.easeFactor ?? null,
    intervalDays: params.progress?.intervalDays ?? 0,
    learningStep: params.progress?.learningStep ?? 0,
    rating: params.rating,
    config: params.config,
    now,
  };

  const next = scheduleNext(input);
  const before = params.progress;

  const row = {
    tutee_id: params.tuteeId,
    student_name: params.studentSlug,
    card_id: params.card.id,
    status: next.status,
    ease_factor: next.easeFactor,
    interval_days: next.intervalDays,
    due_at: next.dueAt.toISOString(),
    learning_step: next.learningStep,
    lapses: (before?.lapses ?? 0) + next.lapsesDelta,
    reps: (before?.reps ?? 0) + next.repsDelta,
    last_rating: params.rating,
    last_reviewed_at: now.toISOString(),
    updated_at: now.toISOString(),
  };

  let saved: FlashcardProgress;

  if (before) {
    const { data, error } = await supabase
      .from('flashcard_progress')
      .update(row)
      .eq('id', before.id)
      .select()
      .single();
    if (error) throw error;
    saved = mapProgress(data);
  } else {
    const { data, error } = await supabase
      .from('flashcard_progress')
      .insert({ ...row, created_at: now.toISOString() })
      .select()
      .single();
    if (error) throw error;
    saved = mapProgress(data);
  }

  await supabase.from('flashcard_review_log').insert({
    tutee_id: params.tuteeId,
    student_name: params.studentSlug,
    card_id: params.card.id,
    rating: params.rating,
    status_before: before?.status ?? 'new',
    status_after: next.status,
    ease_before: before?.easeFactor ?? null,
    ease_after: next.easeFactor,
    interval_before: before?.intervalDays ?? 0,
    interval_after: next.intervalDays,
    reviewed_at: now.toISOString(),
    duration_ms: params.durationMs ?? null,
  });

  return saved;
}

/** Local calendar date YYYY-MM-DD (avoids UTC shift in heatmaps / due counts). */
function dateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export async function fetchFlashcardStats(
  tuteeId: string,
  studentSlug: string,
  studyScope: FlashcardStudyScope = 'all'
): Promise<FlashcardStats> {
  const now = new Date();
  const [allCards, progressList] = await Promise.all([
    fetchFlashcards(tuteeId, studentSlug),
    fetchProgressForStudent(tuteeId, studentSlug),
  ]);
  const cards = filterCardsByStudyScope(allCards, studyScope);
  const cardIdsInScope = new Set(cards.map((c) => c.id));
  const scopedProgress = progressList.filter((p) => cardIdsInScope.has(p.cardId));

  const progressByCard = new Map(scopedProgress.map((p) => [p.cardId, p]));
  let dueNow = 0;
  let matureCards = 0;

  for (const card of cards) {
    const p = progressByCard.get(card.id);
    if (!p) {
      dueNow += 1;
      continue;
    }
    if (new Date(p.dueAt) <= now) dueNow += 1;
    if (p.status === 'review' && p.intervalDays >= 21) matureCards += 1;
  }

  const newToday = countIntroducedToday(scopedProgress, now);

  const { data: logs } = await supabase
    .from('flashcard_review_log')
    .select('rating, reviewed_at, card_id')
    .eq('tutee_id', tuteeId)
    .eq('student_name', studentSlug)
    .gte('reviewed_at', new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString());

  const logRows = (logs || []).filter((l) => cardIdsInScope.has(l.card_id as string));
  const todayKey = dateKey(now);
  const reviewsToday = logRows.filter((l) => dateKey(new Date(l.reviewed_at as string)) === todayKey).length;

  const todayStart = startOfDay(now);
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const ratingCountsByPeriod: Record<FlashcardRatingPeriod, Record<Rating, number>> = {
    today: aggregateRatingCounts(logRows, todayStart),
    week: aggregateRatingCounts(logRows, sevenDaysAgo),
    month: aggregateRatingCounts(logRows, monthAgo),
  };
  const ratingCounts = ratingCountsByPeriod.week;

  const reviewDays = new Set(logRows.map((l) => dateKey(new Date(l.reviewed_at as string))));
  let streak = 0;
  const cursor = new Date(now);
  while (reviewDays.has(dateKey(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }

  const easeValues = scopedProgress
    .filter((p) => p.easeFactor != null)
    .map((p) => p.easeFactor as number);
  const averageEase =
    easeValues.length > 0 ? easeValues.reduce((a, b) => a + b, 0) / easeValues.length : DEFAULT_SM2_CONFIG.defaultEase;

  const forecast: { date: string; count: number }[] = [];
  for (let i = 0; i < 30; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() + i);
    const key = dateKey(d);
    let count = 0;
    for (const card of cards) {
      const p = progressByCard.get(card.id);
      if (!p) {
        if (i === 0) count += 1;
        continue;
      }
      const due = new Date(p.dueAt);
      if (dateKey(due) === key) {
        count += 1;
      } else if (i === 0 && due < now) {
        count += 1;
      }
    }
    forecast.push({ date: key, count });
  }

  const heatmap: { date: string; count: number }[] = [];
  for (let i = 83; i >= 0; i--) {
    const d = startOfDay(now);
    d.setDate(d.getDate() - i);
    const key = dateKey(d);
    const count = logRows.filter((l) => dateKey(new Date(l.reviewed_at as string)) === key).length;
    heatmap.push({ date: key, count });
  }

  return {
    dueNow,
    newToday,
    matureCards,
    totalActive: cards.length,
    reviewsToday,
    streak,
    averageEase,
    ratingCounts,
    ratingCountsByPeriod,
    forecast,
    heatmap,
  };
}

export interface FlashcardCardInsights {
  reviewCount: number;
  isFirstTime: boolean;
  lastSeenLabel: string | null;
  ratingCounts: Record<Rating, number>;
  lapses: number;
  status: CardStatus | 'new';
}

export interface CardReviewSummary {
  reviewCount: number;
  ratingCounts: Record<Rating, number>;
  lastReviewedAt: string | null;
  lastSeenLabel: string | null;
}

function accumulateReviewSummaryRow(
  map: Map<string, CardReviewSummary>,
  row: { card_id: string; rating: string; reviewed_at: string }
): void {
  const cardId = row.card_id;
  let summary = map.get(cardId);
  if (!summary) {
    summary = {
      reviewCount: 0,
      ratingCounts: { again: 0, hard: 0, good: 0, easy: 0 },
      lastReviewedAt: null,
      lastSeenLabel: null,
    };
    map.set(cardId, summary);
  }
  summary.reviewCount += 1;
  const r = row.rating as Rating;
  if (r in summary.ratingCounts) summary.ratingCounts[r] += 1;
  const reviewedAt = row.reviewed_at;
  if (
    !summary.lastReviewedAt ||
    new Date(reviewedAt).getTime() > new Date(summary.lastReviewedAt).getTime()
  ) {
    summary.lastReviewedAt = reviewedAt;
    summary.lastSeenLabel = formatFlashcardLastSeen(reviewedAt);
  }
}

/** Aggregate review-log counts per card for one student. */
export async function fetchCardReviewSummaries(
  tuteeId: string,
  studentSlug: string
): Promise<Map<string, CardReviewSummary>> {
  const { data, error } = await supabase
    .from('flashcard_review_log')
    .select('card_id, rating, reviewed_at')
    .eq('tutee_id', tuteeId)
    .eq('student_name', studentSlug)
    .order('reviewed_at', { ascending: false });

  if (error) throw error;

  const map = new Map<string, CardReviewSummary>();
  for (const row of data || []) {
    accumulateReviewSummaryRow(map, row as { card_id: string; rating: string; reviewed_at: string });
  }
  return map;
}

/** Aggregate review-log counts per card across all students in the group (tutor admin). */
export async function fetchCardReviewSummariesForTutee(
  tuteeId: string
): Promise<Map<string, CardReviewSummary>> {
  const { data, error } = await supabase
    .from('flashcard_review_log')
    .select('card_id, rating, reviewed_at')
    .eq('tutee_id', tuteeId)
    .order('reviewed_at', { ascending: false });

  if (error) throw error;

  const map = new Map<string, CardReviewSummary>();
  for (const row of data || []) {
    accumulateReviewSummaryRow(map, row as { card_id: string; rating: string; reviewed_at: string });
  }
  return map;
}

export async function fetchProgressForTutee(tuteeId: string): Promise<FlashcardProgress[]> {
  const { data, error } = await supabase
    .from('flashcard_progress')
    .select('*')
    .eq('tutee_id', tuteeId);
  if (error) throw error;
  return (data || []).map(mapProgress);
}

/** Best progress row per card when multiple students share the same deck. */
export function mergeProgressByCard(progressList: FlashcardProgress[]): Map<string, FlashcardProgress> {
  const byCard = new Map<string, FlashcardProgress[]>();
  for (const p of progressList) {
    const list = byCard.get(p.cardId) ?? [];
    list.push(p);
    byCard.set(p.cardId, list);
  }
  const merged = new Map<string, FlashcardProgress>();
  for (const [cardId, rows] of byCard) {
    merged.set(
      cardId,
      rows.reduce((best, p) => {
        const bestTime = best.lastReviewedAt ? new Date(best.lastReviewedAt).getTime() : 0;
        const pTime = p.lastReviewedAt ? new Date(p.lastReviewedAt).getTime() : 0;
        if (p.reps > best.reps || (p.reps === best.reps && pTime > bestTime)) return p;
        if (p.lapses > best.lapses) return p;
        return best;
      })
    );
  }
  return merged;
}

export async function fetchCardReviewInsights(
  tuteeId: string,
  studentSlug: string,
  cardId: string,
  progress: FlashcardProgress | null
): Promise<FlashcardCardInsights> {
  const { data: logs, error } = await supabase
    .from('flashcard_review_log')
    .select('rating, reviewed_at')
    .eq('tutee_id', tuteeId)
    .eq('student_name', studentSlug)
    .eq('card_id', cardId)
    .order('reviewed_at', { ascending: false });

  if (error) throw error;
  return buildCardInsightsFromLogs(logs || [], progress);
}

/** Tutor admin: insights across all students who studied this card. */
export async function fetchCardReviewInsightsForTutee(
  tuteeId: string,
  cardId: string,
  progress: FlashcardProgress | null
): Promise<FlashcardCardInsights> {
  const { data: logs, error } = await supabase
    .from('flashcard_review_log')
    .select('rating, reviewed_at')
    .eq('tutee_id', tuteeId)
    .eq('card_id', cardId)
    .order('reviewed_at', { ascending: false });

  if (error) throw error;
  return buildCardInsightsFromLogs(logs || [], progress);
}

function buildCardInsightsFromLogs(
  logs: Array<{ rating: string; reviewed_at: string }>,
  progress: FlashcardProgress | null
): FlashcardCardInsights {
  const ratingCounts: Record<Rating, number> = { again: 0, hard: 0, good: 0, easy: 0 };
  for (const row of logs) {
    const r = row.rating as Rating;
    if (r in ratingCounts) ratingCounts[r]++;
  }

  const reviewCount = logs.length;
  const lastIso =
    (logs.length > 0 ? logs[0].reviewed_at : null) || progress?.lastReviewedAt || null;

  return {
    reviewCount,
    isFirstTime: reviewCount === 0,
    lastSeenLabel: lastIso ? formatFlashcardLastSeen(lastIso) : null,
    ratingCounts,
    lapses: progress?.lapses ?? 0,
    status: progress?.status ?? 'new',
  };
}

export async function fetchHardestCards(
  tuteeId: string,
  studentSlug: string,
  limit = 10
): Promise<Array<{ card: Flashcard; lapses: number }>> {
  const [cards, progressList] = await Promise.all([
    fetchFlashcards(tuteeId, studentSlug),
    fetchProgressForStudent(tuteeId, studentSlug),
  ]);
  return buildHardestCardsList(cards, progressList, limit);
}

export interface FlashcardReviewLogEntry {
  id: string;
  cardId: string;
  cardFront: string;
  cardBack: string;
  rating: Rating;
  reviewedAt: string;
  studentName: string;
}

const REVIEW_LOG_LIMIT = 200;

/** Recent reviews for tutor study-history (newest first). */
export async function fetchFlashcardReviewLog(
  tuteeId: string,
  studentSlug: string,
  options?: { limit?: number; cardIds?: string[] }
): Promise<FlashcardReviewLogEntry[]> {
  const limit = options?.limit ?? REVIEW_LOG_LIMIT;
  const cardIdSet =
    options?.cardIds && options.cardIds.length > 0 && options.cardIds.length <= 80
      ? new Set(options.cardIds)
      : null;

  const { data, error } = await supabase
    .from('flashcard_review_log')
    .select('id, card_id, rating, reviewed_at, student_name, flashcards(front, back)')
    .eq('tutee_id', tuteeId)
    .eq('student_name', studentSlug)
    .order('reviewed_at', { ascending: false })
    .limit(cardIdSet ? limit : Math.min(limit * 3, 500));

  if (error) throw error;

  const mapped = (data || [])
    .map((row) => {
      const card = row.flashcards as { front: string; back: string } | null;
      if (!card?.front) return null;
      return {
        id: row.id as string,
        cardId: row.card_id as string,
        cardFront: card.front,
        cardBack: card.back,
        rating: row.rating as Rating,
        reviewedAt: row.reviewed_at as string,
        studentName: row.student_name as string,
      };
    })
    .filter((e): e is FlashcardReviewLogEntry => e != null);

  const deckFilter =
    options?.cardIds && options.cardIds.length > 0
      ? new Set(options.cardIds)
      : null;

  const filtered = deckFilter ? mapped.filter((e) => deckFilter.has(e.cardId)) : mapped;
  return filtered.slice(0, limit);
}

export async function fetchHardestCardsForTutee(
  tuteeId: string,
  limit = 10
): Promise<Array<{ card: Flashcard; lapses: number }>> {
  const [cards, progressList] = await Promise.all([
    fetchAllFlashcardsForTutee(tuteeId, false),
    fetchProgressForTutee(tuteeId),
  ]);
  return buildHardestCardsList(cards, progressList, limit);
}

function buildHardestCardsList(
  cards: Flashcard[],
  progressList: FlashcardProgress[],
  limit: number
): Array<{ card: Flashcard; lapses: number }> {
  const cardMap = new Map(cards.map((c) => [c.id, c]));
  const lapsesByCard = new Map<string, number>();
  for (const p of progressList) {
    const prev = lapsesByCard.get(p.cardId) ?? 0;
    lapsesByCard.set(p.cardId, Math.max(prev, p.lapses));
  }
  return [...lapsesByCard.entries()]
    .filter(([, lapses]) => lapses > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([cardId, lapses]) => ({ card: cardMap.get(cardId)!, lapses }))
    .filter((x) => x.card);
}

export { parseFlashcardConfig, DEFAULT_SM2_CONFIG };
